import { CURRENCY, FORMATION, SHIP_CLASSES, type ShipClassId, type TradeGoodId } from '@sd/content';
import {
  atWar,
  capitalHex,
  fleetDamage,
  hexKey,
  isArmed,
  techMods,
  tradeTerms,
  type Command,
  type FleetState,
  type GameState,
  type Hex,
  type PlayerState,
} from '@sd/sim';

/** Fleets running a contract's caravan: not for war or settling. */
export const caravanFleets = (state: GameState): Set<number> =>
  new Set(state.contracts.flatMap((c) => (c.caravan ? [c.caravan.fleetId] : [])));

/** A player's armed fleets that are free to fight. */
export function warFleets(state: GameState, playerId: string): FleetState[] {
  const caravans = caravanFleets(state);
  return state.fleets.filter(
    (f) => f.owner === playerId && isArmed(f) && !caravans.has(f.id) && !f.retreating,
  );
}

export const armedShips = (state: GameState, playerId: string): number =>
  warFleets(state, playerId).reduce(
    (n, f) => n + f.ships.filter((s) => s.cls !== 'transport' && s.cls !== 'explorer').length,
    0,
  );

export const strength = (state: GameState, playerId: string): number =>
  warFleets(state, playerId).reduce((sum, f) => sum + fleetDamage(f), 0);

/** Treasury in ГС after conversion. */
export const gsCash = (me: PlayerState): number =>
  (me.cash * me.currency.rate) / (1 + CURRENCY.conversionFee);

export const at = (f: FleetState, h: Hex): boolean => hexKey(f.hex) === hexKey(h);

/** Whether `me` is at war with `other`, or that war is already declared. */
export const warOn = (state: GameState, me: string, other: string): boolean =>
  atWar(state, me, other) ||
  state.pendingWars.some(
    (w) => (w.by === me && w.target === other) || (w.by === other && w.target === me),
  );

/** Wars and declared wars against `me`. */
export const threatened = (state: GameState, me: string): boolean =>
  state.wars.some((w) => w.a === me || w.b === me) ||
  state.pendingWars.some((w) => w.target === me || w.by === me);

/** The warship a faction builds: its own raider for the Reforged, else frigates and corvettes. */
export function warship(me: PlayerState, built: number): ShipClassId {
  if (me.faction === 'reforged' && built % 2 === 0) return 'raider';
  return built % 3 === 2 ? 'corvette' : 'frigate';
}

/** A ship order the treasury and stock allow, keeping `cushion` ГС aside; else null. */
export function orderShip(me: PlayerState, cls: ShipClassId, cushion: number): Command | null {
  if (me.shipyard.length >= 2) return null;
  const cost = SHIP_CLASSES[cls].cost;
  if (gsCash(me) < cost.cash + cushion || me.goods.alloys < cost.alloys) return null;
  return { type: 'buildShip', playerId: me.id, ship: cls };
}

/**
 * Merge the war fleets sitting at `h` into one, as far as «Логистика флота» allows
 * (#160: none without it, four ships at most); null when nothing would merge.
 */
export function mergeAt(state: GameState, playerId: string, h: Hex): Command | null {
  const me = state.players.find((p) => p.id === playerId);
  if (!me || !techMods(me.techs).formations) return null;
  const here = state.fleets
    .filter(
      (f) =>
        f.owner === playerId &&
        at(f, h) &&
        f.path.length === 0 &&
        !caravanFleets(state).has(f.id) &&
        f.ships.every((s) => s.cls !== 'explorer'),
    )
    .sort((a, b) => b.ships.length - a.ships.length || a.id - b.id);
  const ids: number[] = [];
  let ships = 0;
  for (const f of here) {
    if (ships + f.ships.length > FORMATION.maxShips) continue;
    ids.push(f.id);
    ships += f.ships.length;
  }
  return ids.length >= 2 ? { type: 'mergeFleets', playerId, fleetIds: ids } : null;
}

export const home = (state: GameState, playerId: string): Hex => capitalHex(state, playerId);

/** Send a fleet to `to` unless it is there or already on its way. */
export function sendTo(playerId: string, f: FleetState, to: Hex): Command | null {
  if (at(f, to)) return null;
  const end = f.path.at(-1);
  if (end && hexKey(end) === hexKey(to)) return null;
  return { type: 'moveFleet', playerId, fleetId: f.id, to };
}

/**
 * Whether a capital is fed and stocked well enough for discretionary spending
 * (gifts to projects, warships, exotics): a hungry capital buys food first.
 */
export function wellFed(me: PlayerState): boolean {
  const fed = me.planet.lastCycle?.fed.food ?? 1;
  const need = me.planet.lastCycle ? me.planet.lastCycle.consumed.food : 0;
  return fed >= 0.999 && me.goods.food >= need * 2;
}

/**
 * Whether the exchange sells `good` to this player now (#64): an embargo
 * closes a faction's cheap good to its target, and a bot must look elsewhere.
 */
export function canBuy(state: GameState, playerId: string, good: TradeGoodId): boolean {
  const terms = tradeTerms(state, playerId, good, 'buy', 1);
  return terms.ok || terms.reason !== 'embargo';
}
